<template>
	<div class="input-group grow" :class="inputGroupClasses">
		<app-label v-if="label" :field="id ?? name" :inline="inline" data-testid="label">{{
			label
		}}</app-label>
		<div :class="{ 'mt-2': label, 'sm:grow sm:mt-0': inline }">
			<div :class="wrapperClasses" data-testid="wrapper">
				<input
					:id="id ?? name"
					:type="type"
					:name="name"
					:aria-label="name"
					autocomplete="off"
					:class="fieldClasses"
					:disabled="disabled"
					:placeholder="placeholder"
					:value="modelValue"
					:min="minDate"
					data-testid="input"
					@input="onInput" />

				<div
					v-if="message?.showIconWithoutTooltip"
					data-testid="message-icon"
					class="absolute inset-y-0 right-0 flex pr-3 z-10 items-center">
					<app-tooltip
						v-if="tooltips"
						:text="message.text"
						position-x="center"
						:theme="message.theme">
						<font-awesome-icon
							:icon="['fas', message.icon]"
							:class="message.textClass"></font-awesome-icon>
					</app-tooltip>

					<font-awesome-icon
						v-else
						:icon="['fas', message.icon]"
						:class="message.textClass"></font-awesome-icon>
				</div>
			</div>

			<p
				v-if="!tooltips && message"
				:id="`${name}-${message.type}`"
				data-testid="message"
				:class="`mt-2 text-sm ${message.textClass}`">
				{{ message.text }}
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed } from 'vue'
	import { useFieldClasses } from '@/composables/forms'

	const props = withDefaults(
		defineProps<{
			modelValue?: any
			id?: string
			label?: string
			name?: string
			hint?: string
			warning?: string
			tooltips?: boolean
			error?: string
			disabled?: boolean
			placeholder?: string
			inline?: boolean
			size?: 'small' | 'medium' | 'large'
			theme?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger'
			weekdaysOnly?: boolean
			minDate?: Date | string | false
		}>(),
		{
			modelValue: undefined,
			id: '',
			label: undefined,
			name: '',
			tooltips: false,
			hint: undefined,
			warning: undefined,
			error: undefined,
			placeholder: '',
			theme: 'primary',
			size: 'medium',
			inline: false,
			weekdaysOnly: true,
			minDate: ''
		}
	)

	// Styles
	const { wrapperClasses, inputGroupClasses, fieldClasses } = useFieldClasses(props)

	// Events
	const emit = defineEmits(['update:modelValue'])

	function updateValue(value: string) {
		emit('update:modelValue', value)
	}

	function onInput(event: Event) {
		const target = event.target as HTMLInputElement
		updateValue(target.value)
	}

	const type = computed(() => 'date')

	const minDate = computed(() => {
		if (props.minDate) {
			return props.minDate
		}
		// Default to today if no min date specified
		return new Date().toISOString().split('T')[0]
	})

	const message = computed(() => {
		// Error
		if (props.error) {
			return {
				type: 'error',
				text: props.error,
				theme: 'danger',
				icon: 'fa-exclamation-circle',
				textClass: 'text-red-600',
				showIconWithoutTooltip: true
			}
		}

		// Warning
		if (props.warning) {
			return {
				type: 'warning',
				text: props.warning,
				theme: 'warning',
				icon: 'fa-exclamation-triangle',
				textClass: 'text-yellow-400',
				showIconWithoutTooltip: true
			}
		}

		// Info
		if (props.hint) {
			return {
				type: 'description',
				text: props.hint,
				theme: 'dark',
				icon: 'fa-info-circle',
				textClass: 'text-gray-500',
				showIconWithoutTooltip: false
			}
		}

		return undefined
	})
</script>
